// Per-tool icons for the mobile app, mirroring the web app's tool→icon
// mapping in src/config/nav-links.ts. Web uses lucide-react; we use the
// React Native build (lucide-react-native), which renders through the
// already-bundled react-native-svg, so the two platforms stay visually
// identical. Keyed by tool id (see TOOL_NAMES in ../domain/olmps).
import React from 'react';
import {
  Apple,
  BarChart3,
  BookOpenText,
  Bot,
  CalendarDays,
  ClipboardList,
  Eye,
  FolderTree,
  HeartPulse,
  LayoutDashboard,
  Layers,
  ListChecks,
  Megaphone,
  MessageSquareQuote,
  Moon,
  Notebook,
  Orbit,
  Pencil,
  Settings as SettingsIcon,
  Target,
  Wallet,
  Workflow,
  Zap,
  type LucideIcon,
} from 'lucide-react-native';
import { colors } from '../domain/tokens';

// tool id → icon component. Matches the web navLinks icons one-to-one.
export const TOOL_ICONS: Record<string, LucideIcon> = {
  oracle: LayoutDashboard,
  kyrs: ListChecks,
  krnos: CalendarDays,
  metis: ClipboardList,
  clio: BookOpenText,
  astrs: Orbit,
  nemos: Notebook,
  numa: BarChart3,
  tkne: Pencil,
  sofia: Megaphone,
  jnus: Zap,
  telos: Target,
  themis: Workflow,
  hrmez: FolderTree,
  akeso: HeartPulse,
  argos: Eye,
  moyri: Layers,
  syqi: Bot,
  plutus: Wallet,
  hygia: Apple,
  hypnos: Moon,
  epic: MessageSquareQuote,
};

/** Resolve the icon for a tool id, falling back to the generic dashboard glyph. */
export function iconForTool(toolId: string): LucideIcon {
  return TOOL_ICONS[toolId] ?? LayoutDashboard;
}

/** Icon for a resolved sidebar destination (home / settings / a tool). */
export function iconForDest(
  dest: { kind: 'home' } | { kind: 'settings' } | { kind: 'tool'; toolId: string } | null
): LucideIcon {
  if (!dest) return LayoutDashboard;
  if (dest.kind === 'home') return LayoutDashboard;
  if (dest.kind === 'settings') return SettingsIcon;
  return iconForTool(dest.toolId);
}

/** Convenience renderer used by nav lists. */
export function ToolIcon({
  toolId,
  size = 20,
  color = colors.text,
  strokeWidth = 2,
}: {
  toolId: string;
  size?: number;
  color?: string;
  strokeWidth?: number;
}) {
  const Icon = iconForTool(toolId);
  return <Icon size={size} color={color} strokeWidth={strokeWidth} />;
}
